import { useEffect, useState } from 'react';
import { Copy, Check, Download } from 'lucide-react';

// =====================================================================
// /logo — Permanent, public asset page for the Gate 33 monogram.
// Used as a stable image source for email signatures (Gmail/HubSpot),
// press kits, partners, and third-party listings.
// =====================================================================

const PRODUCTION_DOMAIN = 'https://gate33.co';

const VARIANTS = [
  {
    code: 'email',
    label: 'Email signature',
    use: 'Gmail, HubSpot, Apple Mail — recommended for inline signatures.',
    assets: [
      { file: 'logo-email.png', kind: 'PNG', width: 300, height: 300, weight: '11 KB' },
      { file: 'logo-email.jpg', kind: 'JPG', width: 300, height: 300, weight: '5 KB' },
    ],
  },
  {
    code: 'standard',
    label: 'Standard',
    use: 'Web, decks, social posts, internal documents.',
    assets: [
      { file: 'logo.png', kind: 'PNG', width: 800, height: 800, weight: '59 KB' },
      { file: 'logo.jpg', kind: 'JPG', width: 800, height: 800, weight: '17 KB' },
    ],
  },
  {
    code: 'hd',
    label: 'High-resolution',
    use: 'Print, large displays, press, partner kits.',
    assets: [
      { file: 'logo-hd.png', kind: 'PNG', width: 1600, height: 1600, weight: '853 KB' },
    ],
  },
];

const CopyButton = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0F0F0F] border border-[#0F0F0F] px-3 py-2 hover:bg-[#0F0F0F] hover:text-[#FDFBF7] transition-colors duration-200"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? 'Copied' : 'Copy URL'}
    </button>
  );
};

const AssetRow = ({ variant, asset }) => {
  const path = `/img/logo/${asset.file}`;
  const fullUrl = `${PRODUCTION_DOMAIN}${path}`;
  return (
    <div
      data-testid={`logo-asset-${variant.code}-${asset.kind.toLowerCase()}`}
      className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-6 md:gap-8 items-center py-7 border-b border-[#E5E0D8]"
    >
      {/* Preview tile */}
      <div className="w-[160px] h-[160px] flex items-center justify-center bg-[#FDFBF7] border border-[#E5E0D8]">
        <img
          src={path}
          alt={`Gate 33 logo — ${variant.label} ${asset.kind}`}
          className="max-w-[78%] max-h-[78%] object-contain"
        />
      </div>

      {/* Meta */}
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-3 mb-2">
          <span className="overline text-[#1A1A1A]">{variant.label}</span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#9A9A9A]">
            {asset.kind}
          </span>
        </div>
        <div className="text-sm text-[#5A5A5A] mb-3">
          {asset.width} × {asset.height} px · {asset.kind} · {asset.weight} · Ivory background <span className="font-mono text-[#0F0F0F]">#FDFBF7</span>
        </div>
        <div className="font-mono text-[12px] text-[#0F0F0F] break-all bg-[#FDFBF7] border border-[#E5E0D8] px-3 py-2">
          {fullUrl}
        </div>
      </div>

      {/* Actions */}
      <div className="flex md:flex-col gap-2 md:items-end">
        <CopyButton value={fullUrl} testId={`logo-copy-${variant.code}-${asset.kind.toLowerCase()}`} />
        <a
          href={path}
          download
          data-testid={`logo-download-${variant.code}-${asset.kind.toLowerCase()}`}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0F0F0F] border border-[#C5A880] bg-[#C5A880] px-3 py-2 hover:bg-[#0F0F0F] hover:text-[#FDFBF7] hover:border-[#0F0F0F] transition-colors duration-200"
        >
          <Download size={13} />
          Download
        </a>
      </div>
    </div>
  );
};

const LogoPage = () => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'Brand Logo · Gate 33';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  return (
    <div data-testid="logo-page" className="min-h-screen bg-white">
      {/* ===== HERO ===== */}
      <section data-testid="logo-hero" className="section-padding bg-[#FDFBF7] border-b border-[#E5E0D8]">
        <div className="container-custom max-w-5xl">
          <span className="overline inline-block mb-6">Brand Assets</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
            Gate 33 — Official Logo
          </h1>
          <p className="text-lg text-[#5A5A5A] leading-relaxed max-w-2xl">
            Permanent, public URLs for the Gate 33 monogram. Use these direct links in
            email signatures, partner documents, and any context that requires a stable
            image source.
          </p>
        </div>
      </section>

      {/* ===== ASSETS ===== */}
      <section data-testid="logo-assets" className="section-padding bg-white">
        <div className="container-custom max-w-5xl">
          <div className="max-w-2xl mb-12">
            <span className="overline inline-block mb-4">Available Variants</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-4 text-[#0F0F0F]">
              Choose the right file
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Every variant is square (1:1) and presented on the Gate 33 ivory background
              (<span className="font-mono text-sm text-[#0F0F0F]">#FDFBF7</span>) so the
              monogram renders consistently across email clients and documents.
            </p>
          </div>

          {VARIANTS.map((v) => (
            <div key={v.code} data-testid={`logo-variant-${v.code}`} className="mb-14">
              <div className="flex flex-wrap items-baseline gap-3 mb-4 pb-4 border-b-2 border-[#0F0F0F]">
                <h3 className="font-heading text-2xl text-[#0F0F0F]">{v.label}</h3>
                <span className="text-sm text-[#5A5A5A]">{v.use}</span>
              </div>
              {v.assets.map((a) => (
                <AssetRow key={a.file} variant={v} asset={a} />
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ===== USAGE — GMAIL & HUBSPOT ===== */}
      <section data-testid="logo-usage" className="section-padding bg-[#FDFBF7] border-t border-[#E5E0D8]">
        <div className="container-custom max-w-5xl">
          <span className="overline inline-block mb-4">Quick Guide</span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-10 text-[#0F0F0F]">
            How to use in Gmail & HubSpot
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div data-testid="logo-guide-gmail" className="border-l-2 border-[#C5A880] pl-6">
              <h3 className="font-heading text-xl text-[#0F0F0F] mb-4">Gmail signature</h3>
              <ol className="space-y-3 text-sm text-[#5A5A5A] leading-relaxed list-decimal pl-4">
                <li>Gmail → Settings (gear icon) → <em>See all settings</em>.</li>
                <li>Scroll to <em>Signature</em> and click <em>Create new</em>.</li>
                <li>Click the image icon in the signature editor.</li>
                <li>Select <em>Web Address (URL)</em> and paste the <strong>Email signature</strong> JPG URL above.</li>
                <li>Resize the inserted image to <strong>96 × 96 px</strong>. Save changes.</li>
              </ol>
            </div>

            <div data-testid="logo-guide-hubspot" className="border-l-2 border-[#C5A880] pl-6">
              <h3 className="font-heading text-xl text-[#0F0F0F] mb-4">HubSpot signature</h3>
              <ol className="space-y-3 text-sm text-[#5A5A5A] leading-relaxed list-decimal pl-4">
                <li>Profile → <em>Profile & Preferences</em> → Email signature.</li>
                <li>Switch to <em>HTML</em> mode.</li>
                <li>
                  Paste this snippet:
                  <pre className="font-mono text-[12px] bg-white border border-[#E5E0D8] px-3 py-2 mt-2 overflow-x-auto whitespace-pre-wrap break-all">
{`<img src="${PRODUCTION_DOMAIN}/img/logo/logo-email.jpg" width="96" height="96" alt="Gate 33" />`}
                  </pre>
                </li>
                <li>Save. The image is served from a stable, permanent URL.</li>
              </ol>
            </div>
          </div>

          <div className="mt-12 p-6 border border-[#E5E0D8] bg-white">
            <p className="text-sm text-[#5A5A5A] leading-relaxed">
              <span className="font-heading text-[#0F0F0F]">Note —</span> these URLs are
              permanent. They will not expire, rotate, or change. If a revision of the
              logo is ever produced, the existing files will be updated in place so every
              email signature already in circulation continues to render correctly.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LogoPage;
